import i18n from 'src/i18n';
import { useCallback, useEffect, useRef, useState } from 'react';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import WidgetsIcon from '@mui/icons-material/Widgets';
import {
  Avatar,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Config } from 'src/config';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { addLiveChatSocketListener, connectLiveChatSocket } from 'src/utils/liveChatSocket';
import { useReAuth } from 'src/utils/reAuth/ReAuthContext';
import { useToaster } from 'src/utils/toaster/toasterContext';
import IntegratedAccounts from './components/integratedAccounts.component';
import IntegrationCard from './components/integrationCard.component';

const channels = [
  {
    key: 'website-widget',
    titleKey: 'website_widget.title',
    titleFallback: 'Website Widget',
    descriptionKey: 'website_widget.description',
    descriptionFallback: 'Set up your website chat widget and manage customer conversations from your site.',
    actionKey: 'website_widget.action',
    actionFallback: 'Open Widget',
    path: '/widget',
    Icon: WidgetsIcon,
    color: '#00A6A6',
    comingSoon: false,
  },
  {
    key: 'whatsapp',
    titleKey: 'whatsapp.title',
    titleFallback: 'WhatsApp',
    descriptionKey: 'whatsapp.description',
    descriptionFallback: 'Connect WhatsApp Business to manage customer conversations from Brand2Chat.',
    actionKey: 'whatsapp.action',
    actionFallback: 'Connect WhatsApp',
    Icon: WhatsAppIcon,
    color: '#25D366',
    comingSoon: false,
  },
  {
    key: 'facebook',
    titleKey: 'facebook.title',
    titleFallback: 'Facebook',
    descriptionKey: 'facebook.description',
    descriptionFallback: 'Connect Facebook pages and messages to bring social conversations into one inbox.',
    Icon: FacebookIcon,
    color: '#1877F2',
  },
  {
    key: 'instagram',
    titleKey: 'instagram.title',
    titleFallback: 'Instagram',
    descriptionKey: 'instagram.description',
    descriptionFallback: 'Connect Instagram messages and comments so your social support stays in one place.',
    actionKey: 'instagram.action',
    actionFallback: 'Connect Instagram',
    Icon: InstagramIcon,
    color: '#E1306C',
    comingSoon: false,
  },
];

const centeredPopupFeatures = (features = '') => {
  const width = Math.min(720, Math.max(1, window.screen.availWidth - 32));
  const height = Math.min(760, Math.max(1, window.screen.availHeight - 32));
  const left = Math.round(window.screenX + (window.outerWidth - width) / 2);
  const top = Math.round(window.screenY + (window.outerHeight - height) / 2);
  const otherFeatures = String(features)
    .split(',')
    .map((feature) => feature.trim())
    .filter((feature) => feature && !/^(width|height|left|top|screenx|screeny)\s*=/i.test(feature));

  return [...otherFeatures, `width=${width}`, `height=${height}`, `left=${left}`, `top=${top}`].join(',');
};

const openMetaOAuth = (url, popupName) => {
  if (CommonHelper.CustomDomain()) {
    const popup = window.open(url, popupName, centeredPopupFeatures());
    if (popup) {
      popup.focus();
      return;
    }
  }
  window.location.href = url;
};

const startFacebookOAuthRedirect = ({ mode = 'connect', pageID = '' } = {}) => {
  const redirectURL = CommonHelper.GetMetaOAuthRedirectUri('/facebook-redirect');
  window.localStorage.setItem('facebook_oauth_redirect_uri', redirectURL);
  window.localStorage.setItem('facebook_oauth_mode', mode);
  if (pageID) {
    window.localStorage.setItem('facebook_oauth_page_id', pageID);
  } else {
    window.localStorage.removeItem('facebook_oauth_page_id');
  }
  window.localStorage.removeItem('facebook_oauth_account_id');
  if (mode === 'reauth') {
    window.sessionStorage.removeItem('facebook_integration_pages');
  }

  const params = new URLSearchParams({
    client_id: String(Config.Facebook.appId),
    redirect_uri: redirectURL,
    scope: Config.Facebook.scope,
    response_type: 'code',
    auth_type: mode === 'reauth' ? 'reauthorize' : 'rerequest',
    state: CommonHelper.BuildMetaOAuthState({ service: 'facebook', mode, page_id: pageID }),
  });

  openMetaOAuth(
    `https://www.facebook.com/${Config.Facebook.version}/dialog/oauth?${params.toString()}`,
    'facebook-oauth'
  );
};

const startInstagramOAuthRedirect = ({ mode = 'connect', instagramID = '' } = {}) => {
  const redirectURL = CommonHelper.GetMetaOAuthRedirectUri('/instagram-redirect');
  window.localStorage.setItem('instagram_oauth_redirect_uri', redirectURL);
  window.localStorage.setItem('instagram_oauth_mode', mode);
  if (instagramID) {
    window.localStorage.setItem('instagram_oauth_instagram_id', instagramID);
  } else {
    window.localStorage.removeItem('instagram_oauth_instagram_id');
  }
  window.localStorage.removeItem('instagram_oauth_account_id');
  if (mode === 'reauth') {
    window.sessionStorage.removeItem('instagram_integration_accounts');
  }

  const params = new URLSearchParams({
    client_id: String(Config.Instagram.appId),
    redirect_uri: redirectURL,
    scope: Config.Instagram.scope,
    response_type: 'code',
    auth_type: mode === 'reauth' ? 'reauthorize' : 'rerequest',
    state: CommonHelper.BuildMetaOAuthState({ service: 'instagram', mode, instagram_id: instagramID }),
  });

  openMetaOAuth(`${Config.Instagram.authorizeUrl}?${params.toString()}`, 'instagram-oauth');
};
const loadFacebookSDK = (appID, sdkConfig = Config.Facebook) =>
  new Promise((resolve, reject) => {
    if (window.FB) {
      resolve(window.FB);
      return;
    }
    window.fbAsyncInit = function () {
      window.FB.init({
        appId: appID,
        autoLogAppEvents: true,
        xfbml: true,
        version: sdkConfig.version,
      });
      resolve(window.FB);
    };
    const existingScript = document.getElementById(sdkConfig.SCRIPT_ID || 'facebook-jssdk');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.FB), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Facebook SDK could not be loaded')), {
        once: true,
      });
      return;
    }
    const script = document.createElement('script');
    script.id = sdkConfig.SCRIPT_ID || 'facebook-jssdk';
    script.src = sdkConfig.SDK_URL;
    script.async = true;
    script.defer = true;
    script.onerror = () => reject(new Error('Facebook SDK could not be loaded'));
    document.body.appendChild(script);
  });

export default function IntegrationsPage() {
  const { t: translate } = useTranslation();
  const { showToast } = useToaster();
  const location = useLocation();
  const reAuthContext = useReAuth();
  const syncInactiveProfiles = reAuthContext?.syncInactiveProfiles;
  const clearInactiveProfile = reAuthContext?.clearInactiveProfile;
  const headerWhatsAppReauthRef = useRef(false);
  const [whatsAppSetup, setWhatsAppSetup] = useState(null);
  const [whatsAppLoader, setWhatsAppLoader] = useState(false);
  const [whatsAppPinDialogOpen, setWhatsAppPinDialogOpen] = useState(false);
  const [whatsAppPin, setWhatsAppPin] = useState('');
  const [whatsAppPendingAccountID, setWhatsAppPendingAccountID] = useState(null);
  const [facebookLoader, setFacebookLoader] = useState(false);
  const [instagramLoader, setInstagramLoader] = useState(false);
  const [instagramDialogOpen, setInstagramDialogOpen] = useState(false);
  const [instagramAuthSession, setInstagramAuthSession] = useState('');
  const [instagramAccountOptions, setInstagramAccountOptions] = useState([]);
  const [selectedInstagramIDs, setSelectedInstagramIDs] = useState([]);
  const [instagramAuthPagination, setInstagramAuthPagination] = useState('');
  const [instagramAccountPaginationLoader, setInstagramAccountPaginationLoader] = useState(false);
  const [facebookDialogOpen, setFacebookDialogOpen] = useState(false);
  const [facebookAuthSession, setFacebookAuthSession] = useState('');
  const [facebookPageOptions, setFacebookPageOptions] = useState([]);
  const [selectedFacebookPageIDs, setSelectedFacebookPageIDs] = useState([]);
  const [facebookAuthPagination, setFacebookAuthPagination] = useState('');
  const [facebookPagePaginationLoader, setFacebookPagePaginationLoader] = useState(false);
  const [activeTab, setActiveTab] = useState(() =>
    new URLSearchParams(window.location.search).get('tab') === 'accounts' ? 'accounts' : 'integrations'
  );
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [connectedProfiles, setConnectedProfiles] = useState([]);
  const [connectedProfilesLoader, setConnectedProfilesLoader] = useState(false);
  const [refreshingAccount, setRefreshingAccount] = useState('');
  const whatsAppSetupRef = useRef(null);
  const whatsAppAuthRef = useRef({ code: '', completed: false, submitted: false, timeoutId: null, reauthAccount: null });

  useEffect(() => {
    whatsAppSetupRef.current = whatsAppSetup;
  }, [whatsAppSetup]);

  const fetchConnectedProfiles = useCallback(
    (silent = false) => {
      if (!silent) setConnectedProfilesLoader(true);
      HttpServices.Get({
        url: userModuleURL.connectedProfiles,
        successCallback: (data) => {
          const accounts = data?.accounts || [];
          setConnectedProfiles(accounts);
          syncInactiveProfiles?.(accounts);
          if (!silent) setConnectedProfilesLoader(false);
        },
        failureCallback: (message) => {
          if (!silent) setConnectedProfilesLoader(false);
          showToast(message || 'Failed to fetch connected profiles', 'e');
        },
      });
    },
    [showToast, syncInactiveProfiles]
  );

  const clearFacebookOAuthStorage = useCallback(() => {
    window.localStorage.removeItem('facebook_oauth_redirect_uri');
    window.localStorage.removeItem('facebook_oauth_mode');
    window.localStorage.removeItem('facebook_oauth_account_id');
    window.localStorage.removeItem('facebook_oauth_page_id');
  }, []);

  const completeFacebookOAuth = useCallback(
    (code, redirectURL, pageID = '') => {
      if (!code) {
        showToast('Meta did not return a Facebook authorization code.', 'e');
        return;
      }
      setFacebookLoader(true);
      if (pageID) {
        HttpServices.Post({
          url: userModuleURL.facebookReauthenticate,
          body: {
            code,
            page_id: pageID,
            redirect_url: redirectURL,
          },
          successCallback: (_data, message) => {
            clearFacebookOAuthStorage();
            setFacebookLoader(false);
            showToast(message || 'Facebook page re-authenticated successfully.', 's');
            fetchConnectedProfiles(true);
            clearInactiveProfile?.('facebook', pageID);
            setActiveTab('accounts');
          },
          failureCallback: (message) => {
            clearFacebookOAuthStorage();
            setFacebookLoader(false);
            showToast(message || 'Facebook page re-authentication failed.', 'e');
          },
        });
        return;
      }

      HttpServices.Post({
        url: userModuleURL.facebookAuth,
        body: {
          code,
          redirect_url: redirectURL,
        },
        successCallback: (data) => {
          clearFacebookOAuthStorage();
          setFacebookLoader(false);
          setFacebookAuthSession(data?.session_id || '');
          setFacebookPageOptions(data?.pages || []);
          setSelectedFacebookPageIDs([]);
          setFacebookAuthPagination(data?.pagination || '');
          setFacebookDialogOpen(true);
          setActiveTab('accounts');
        },
        failureCallback: (message) => {
          clearFacebookOAuthStorage();
          setFacebookLoader(false);
          showToast(message || 'Facebook connection failed.', 'e');
        },
      });
    },
    [clearFacebookOAuthStorage, clearInactiveProfile, fetchConnectedProfiles, showToast]
  );

  const clearInstagramOAuthStorage = useCallback(() => {
    window.localStorage.removeItem('instagram_oauth_redirect_uri');
    window.localStorage.removeItem('instagram_oauth_mode');
    window.localStorage.removeItem('instagram_oauth_account_id');
    window.localStorage.removeItem('instagram_oauth_instagram_id');
  }, []);

  const completeInstagramOAuth = useCallback(
    (code, redirectURL, instagramID = '') => {
      if (!code) {
        showToast('Meta did not return an Instagram authorization code.', 'e');
        return;
      }
      setInstagramLoader(true);
      if (instagramID) {
        HttpServices.Post({
          url: userModuleURL.instagramReauthenticate,
          body: {
            code,
            instagram_id: instagramID,
            redirect_url: redirectURL,
          },
          successCallback: (_data, message) => {
            clearInstagramOAuthStorage();
            setInstagramLoader(false);
            showToast(message || 'Instagram account re-authenticated successfully.', 's');
            fetchConnectedProfiles(true);
            clearInactiveProfile?.('instagram', instagramID);
            setActiveTab('accounts');
          },
          failureCallback: (message) => {
            clearInstagramOAuthStorage();
            setInstagramLoader(false);
            showToast(message || 'Instagram account re-authentication failed.', 'e');
          },
        });
        return;
      }

      HttpServices.Post({
        url: userModuleURL.instagramAuth,
        body: {
          code,
          redirect_url: redirectURL,
        },
        successCallback: (data) => {
          clearInstagramOAuthStorage();
          setInstagramLoader(false);
          setInstagramAuthSession(data?.session_id || '');
          setInstagramAccountOptions(data?.accounts || []);
          setSelectedInstagramIDs([]);
          setInstagramAuthPagination(data?.pagination || '');
          setInstagramDialogOpen(true);
          setActiveTab('accounts');
        },
        failureCallback: (message) => {
          clearInstagramOAuthStorage();
          setInstagramLoader(false);
          showToast(message || 'Instagram connection failed.', 'e');
        },
      });
    },
    [clearInstagramOAuthStorage, clearInactiveProfile, fetchConnectedProfiles, showToast]
  );

  const connectWhatsApp = useCallback(
    (payload, reauthAccount = null) => {
      if (!CommonHelper.HasProfessionalPlan()) {
        showToast(i18n.t('plan_access.whatsapp_upgrade'), 'e');
        return;
      }
      setWhatsAppLoader(true);
      HttpServices.Post({
        url: reauthAccount ? userModuleURL.whatsappReauthenticate : userModuleURL.whatsappConnect,
        body: reauthAccount
          ? {
              code: payload.code,
              account_id: reauthAccount.id,
              phone_number_id: payload.phone_number_id || reauthAccount.profile_id,
              waba_id: payload.waba_id || reauthAccount.waba_id,
            }
          : payload,
        successCallback: (data, message) => {
          setWhatsAppLoader(false);
          if (data?.registration_required) {
            setWhatsAppPendingAccountID(data.id);
            setWhatsAppPinDialogOpen(true);
            fetchConnectedProfiles(true);
            return;
          }
          showToast(
            message || (reauthAccount ? 'WhatsApp account re-authenticated successfully' : 'WhatsApp account connected successfully'),
            's'
          );
          fetchConnectedProfiles(true);
          if (reauthAccount) clearInactiveProfile?.('whatsapp', reauthAccount.profile_id);
        },
        failureCallback: (message) => {
          setWhatsAppLoader(false);
          showToast(message || (reauthAccount ? 'WhatsApp account re-authentication failed' : 'WhatsApp connection failed'), 'e');
        },
      });
    },
    [clearInactiveProfile, fetchConnectedProfiles, showToast]
  );

  const submitWhatsAppSignup = useCallback(() => {
    const auth = whatsAppAuthRef.current;
    const setup = whatsAppSetupRef.current || {};
    if (auth.submitted || !auth.code || !auth.completed || !setup.pending_waba_id) return;
    auth.submitted = true;
    window.clearTimeout(auth.timeoutId);
    connectWhatsApp({
      code: auth.code,
      waba_id: setup.pending_waba_id,
      phone_number_id: setup.pending_phone_number_id,
      business_account_id: setup.pending_business_account_id || '',
      connection_type: setup.pending_connection_type || 'waba',
      history_session_id: setup.pending_history_session_id || '',
    }, auth.reauthAccount);
  }, [connectWhatsApp]);

  useEffect(() => {
    fetchConnectedProfiles();

    const pendingFacebookPages = window.sessionStorage.getItem('facebook_integration_pages');
    if (pendingFacebookPages) {
      try {
        const data = JSON.parse(pendingFacebookPages);
        setFacebookAuthSession(data?.session_id || '');
        setFacebookPageOptions(data?.pages || []);
        setSelectedFacebookPageIDs([]);
        setFacebookAuthPagination(data?.pagination || '');
        setFacebookDialogOpen(true);
        setActiveTab('accounts');
      } catch (_error) {
        showToast('Facebook page selection data could not be loaded.', 'e');
      }
      window.sessionStorage.removeItem('facebook_integration_pages');
    }

    const pendingInstagramAccounts = window.sessionStorage.getItem('instagram_integration_accounts');
    if (pendingInstagramAccounts) {
      try {
        const data = JSON.parse(pendingInstagramAccounts);
        setInstagramAuthSession(data?.session_id || '');
        setInstagramAccountOptions(data?.accounts || []);
        setSelectedInstagramIDs([]);
        setInstagramAuthPagination(data?.pagination || '');
        setInstagramDialogOpen(true);
        setActiveTab('accounts');
      } catch (_error) {
        showToast('Instagram account selection data could not be loaded.', 'e');
      }
      window.sessionStorage.removeItem('instagram_integration_accounts');
    }
    const onMessage = (event) => {
      let data = event.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (_err) {
          return;
        }
      }

      if (data?.type === 'brand2chat:meta-oauth-code') {
        if (!CommonHelper.IsTrustedMetaOAuthMessageOrigin(event.origin)) return;
        if (data?.error) {
          showToast(data.error, 'e');
          return;
        }
        const service = data?.service || '';
        if (CommonHelper.MarkMetaOAuthCodeHandled(service, data?.code || '')) return;
        if (service === 'facebook') {
          completeFacebookOAuth(
            data?.code || '',
            data?.redirect_url || CommonHelper.GetMetaOAuthRedirectUri('/facebook-redirect'),
            data?.page_id || data?.account_id || ''
          );
        }
        if (service === 'instagram') {
          completeInstagramOAuth(
            data?.code || '',
            data?.redirect_url || CommonHelper.GetMetaOAuthRedirectUri('/instagram-redirect'),
            data?.instagram_id || data?.account_id || ''
          );
        }
        return;
      }

      let originHost = '';
      try {
        originHost = new URL(event.origin).hostname;
      } catch (_error) {
        return;
      }
      if (originHost !== 'facebook.com' && !originHost.endsWith('.facebook.com')) return;
      const eventName = String(data?.event || '').toUpperCase();
      const embeddedSignup = data?.type === 'WA_EMBEDDED_SIGNUP';
      if (!embeddedSignup) return;
      if (eventName === 'CANCEL' || eventName === 'ERROR') {
        window.clearTimeout(whatsAppAuthRef.current.timeoutId);
        showToast('WhatsApp connection was not completed in Meta.', 'e');
        return;
      }
      if (eventName !== 'FINISH' && eventName !== 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING') return;
      const payload = data?.data || data?.payload || {};
      const wabaID = payload.waba_id || payload.whatsapp_business_account_id || payload.business_account_id;
      const phoneNumberID = payload.phone_number_id || payload.phone_id;
      const businessAccountID = payload.business_account_id || payload.business_id || '';
      const historySessionID = payload.session_id || payload.history_session_id || data?.session_id || '';
      const rawConnectionType = String(payload.connection_type || payload.integration_type || '').toLowerCase();
      const connectionType =
        eventName === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING' ||
        rawConnectionType === 'business_app' ||
        rawConnectionType === 'coexistence'
          ? 'business_app'
          : 'waba';
      const currentSetup = whatsAppSetupRef.current || {};
      const nextSetup = {
        ...currentSetup,
        pending_waba_id: wabaID || currentSetup.pending_waba_id || '',
        pending_phone_number_id: phoneNumberID || currentSetup.pending_phone_number_id || '',
        pending_business_account_id: businessAccountID || currentSetup.pending_business_account_id || '',
        pending_history_session_id: historySessionID || currentSetup.pending_history_session_id || '',
        pending_connection_type: connectionType || currentSetup.pending_connection_type || 'waba',
      };
      whatsAppSetupRef.current = nextSetup;
      setWhatsAppSetup(nextSetup);
      whatsAppAuthRef.current.completed = true;
      submitWhatsAppSignup();
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [completeFacebookOAuth, completeInstagramOAuth, fetchConnectedProfiles, showToast, submitWhatsAppSignup]);

  useEffect(() => {
    connectLiveChatSocket();
    const removeListener = addLiveChatSocketListener((data) => {
      if (data.type === 'facebook:message:new' || data.type === 'instagram:message:new') {
        fetchConnectedProfiles(true);
      }
    });
    return removeListener;
  }, [fetchConnectedProfiles]);

  const handleWhatsAppConnect = useCallback(async (account = null) => {
    if (!CommonHelper.HasProfessionalPlan()) {
      showToast(i18n.t('plan_access.whatsapp_upgrade'), 'e');
      return;
    }
    const whatsAppAppID = String(Config.WhatsApp.appId || '');
    const whatsAppConfigID = String(Config.WhatsApp.embeddedSignupConfigId || '');
    if (!whatsAppAppID || !whatsAppConfigID) {
      showToast('Add WhatsApp app id and embedded signup config id in backend config first.', 'e');
      return;
    }
    window.clearTimeout(whatsAppAuthRef.current.timeoutId);
    const reauthAccount = account?.profile_id ? account : null;
    whatsAppAuthRef.current = { code: '', completed: false, submitted: false, timeoutId: null, reauthAccount };
    setWhatsAppPin('');
    setWhatsAppPinDialogOpen(false);
    const initialSetup = reauthAccount
      ? {
          pending_waba_id: reauthAccount.waba_id || '',
          pending_phone_number_id: reauthAccount.profile_id || '',
          pending_business_account_id: reauthAccount.business_account_id || '',
          pending_connection_type: reauthAccount.connection_type || 'waba',
        }
      : null;
    whatsAppSetupRef.current = initialSetup;
    setWhatsAppSetup(initialSetup);
    try {
      const FB = await loadFacebookSDK(whatsAppAppID, Config.WhatsApp);
      const originalOpen = window.open;
      let restoreTimer;
      const restoreOpen = () => {
        if (window.open === centeredOpen) window.open = originalOpen;
        window.clearTimeout(restoreTimer);
      };
      const centeredOpen = (url, name, features) => {
        const popup = originalOpen.call(window, url, name, centeredPopupFeatures(features));
        restoreOpen();
        return popup;
      };
      window.open = centeredOpen;
      restoreTimer = window.setTimeout(restoreOpen, 5000);
      try {
        FB.login(
          (response) => {
            restoreOpen();
            const code = response?.authResponse?.code;
            if (!code) {
              if (!whatsAppAuthRef.current.completed) showToast('WhatsApp connection was not completed in Meta.', 'e');
              return;
            }
            whatsAppAuthRef.current.code = code;
            submitWhatsAppSignup();
            if (!whatsAppAuthRef.current.submitted) {
              whatsAppAuthRef.current.timeoutId = window.setTimeout(() => {
                if (!whatsAppAuthRef.current.submitted)
                  showToast('Meta did not finish WhatsApp onboarding. Complete the final step and try again.', 'e');
              }, 120000);
            }
          },
          {
            config_id: whatsAppConfigID,
            response_type: 'code',
            override_default_response_type: true,
            extras: {
              setup: {},
              featureType: 'whatsapp_business_app_onboarding',
              sessionInfoVersion: '3',
            },
          }
        );
      } catch (sdkError) {
        restoreOpen();
        throw sdkError;
      }
    } catch (err) {
      showToast(err.message || 'Facebook SDK could not be loaded', 'e');
    }
  }, [showToast, submitWhatsAppSignup]);

  useEffect(() => {
    if (headerWhatsAppReauthRef.current || location.state?.social_reauth_service !== 'whatsapp') return;
    headerWhatsAppReauthRef.current = true;
    setActiveTab('accounts');
    handleWhatsAppConnect({ profile_id: location.state?.profile_id || '' });
    window.history.replaceState({}, document.title, window.location.pathname);
  }, [handleWhatsAppConnect, location.state]);

  const handleFacebookConnect = () => {
    const facebookAppID = String(Config.Facebook.appId || '');
    if (!facebookAppID) {
      showToast('Add Facebook app id in backend config first.', 'e');
      return;
    }
    startFacebookOAuthRedirect();
  };

  const handleInstagramConnect = () => {
    const instagramAppID = String(Config.Instagram.appId || '');
    if (!instagramAppID) {
      showToast('Add Instagram app id in backend config first.', 'e');
      return;
    }
    startInstagramOAuthRedirect();
  };

  const loadNextFacebookAuthPages = () => {
    if (!facebookAuthSession || !facebookAuthPagination) return;
    setFacebookPagePaginationLoader(true);
    HttpServices.Get({
      url: `${userModuleURL.facebookAuthPages}?session_id=${encodeURIComponent(
        facebookAuthSession
      )}&after=${encodeURIComponent(facebookAuthPagination)}`,
      successCallback: (data) => {
        setFacebookPageOptions(data?.pages || []);
        setSelectedFacebookPageIDs([]);
        setFacebookAuthPagination(data?.pagination || '');
        setFacebookPagePaginationLoader(false);
      },
      failureCallback: (message) => {
        setFacebookPagePaginationLoader(false);
        showToast(message || 'Failed to fetch next Facebook pages', 'e');
      },
    });
  };

  const loadNextInstagramAuthAccounts = () => {
    if (!instagramAuthSession || !instagramAuthPagination) return;
    setInstagramAccountPaginationLoader(true);
    HttpServices.Get({
      url: `${userModuleURL.instagramAuthAccounts}?session_id=${encodeURIComponent(
        instagramAuthSession
      )}&after=${encodeURIComponent(instagramAuthPagination)}`,
      successCallback: (data) => {
        setInstagramAccountOptions(data?.accounts || []);
        setSelectedInstagramIDs([]);
        setInstagramAuthPagination(data?.pagination || '');
        setInstagramAccountPaginationLoader(false);
      },
      failureCallback: (message) => {
        setInstagramAccountPaginationLoader(false);
        showToast(message || 'Failed to fetch next Instagram accounts', 'e');
      },
    });
  };
  const integrateInstagramAccounts = () => {
    if (!instagramAuthSession || selectedInstagramIDs.length === 0) {
      showToast('Select at least one Instagram account.', 'e');
      return;
    }
    setInstagramLoader(true);
    HttpServices.Post({
      url: userModuleURL.instagramIntegrateAccounts,
      body: {
        session_id: instagramAuthSession,
        instagram_ids: selectedInstagramIDs,
      },
      successCallback: (_data, message) => {
        setInstagramDialogOpen(false);
        setInstagramAccountOptions([]);
        setSelectedInstagramIDs([]);
        setInstagramAuthSession('');
        setInstagramAuthPagination('');
        setInstagramLoader(false);
        showToast(message || 'Instagram accounts integrated successfully', 's');
        fetchConnectedProfiles(true);
      },
      failureCallback: (message) => {
        setInstagramLoader(false);
        showToast(message || 'Instagram account integration failed', 'e');
      },
    });
  };

  const integrateFacebookPages = () => {
    if (!facebookAuthSession || selectedFacebookPageIDs.length === 0) {
      showToast('Select at least one Facebook page.', 'e');
      return;
    }
    setFacebookLoader(true);
    HttpServices.Post({
      url: userModuleURL.facebookIntegratePages,
      body: {
        session_id: facebookAuthSession,
        page_ids: selectedFacebookPageIDs,
      },
      successCallback: (_data, message) => {
        setFacebookDialogOpen(false);
        setFacebookPageOptions([]);
        setSelectedFacebookPageIDs([]);
        setFacebookAuthSession('');
        setFacebookAuthPagination('');
        setFacebookLoader(false);
        showToast(message || 'Facebook pages integrated successfully', 's');
        fetchConnectedProfiles(true);
      },
      failureCallback: (message) => {
        setFacebookLoader(false);
        showToast(message || 'Facebook page integration failed', 'e');
      },
    });
  };

  const reauthenticateFacebookPage = (account) => {
    startFacebookOAuthRedirect({ mode: 'reauth', pageID: account.profile_id });
  };

  const reauthenticateWhatsAppAccount = (account) => {
    handleWhatsAppConnect(account);
  };

  const reauthenticateInstagramAccount = (account) => {
    startInstagramOAuthRedirect({ mode: 'reauth', instagramID: account.profile_id });
  };

  const updateAccountEnable = (service, account, isEnable) => {
    const accountID = account.profile_id || account.id;
    if (account?.is_active === false && isEnable) {
      showToast('This account is inactive. Re-authenticate or reconnect it first.', 'e');
      return;
    }
    if (!accountID) return;
    HttpServices.Post({
      url: userModuleURL.connectedProfileEnable(service, encodeURIComponent(accountID)),
      body: { is_enable: isEnable },
      successCallback: (_data, message) => {
        showToast(message || `${service} account ${isEnable ? 'enabled' : 'disabled'} successfully`, 's');
        fetchConnectedProfiles(true);
      },
      failureCallback: (message) => {
        showToast(message || `Failed to ${isEnable ? 'enable' : 'disable'} ${service} account`, 'e');
        fetchConnectedProfiles(true);
      },
    });
  };

  const refreshAccountProfile = (service, account) => {
    const accountID = Number(account?.id || 0);
    if (!accountID) return;
    const refreshURLs = {
      facebook: userModuleURL.facebookAccountRefresh,
      instagram: userModuleURL.instagramAccountRefresh,
      whatsapp: userModuleURL.whatsappAccountRefresh,
    };
    const buildURL = refreshURLs[service];
    if (!buildURL) return;
    const refreshKey = `${service}:${accountID}`;
    setRefreshingAccount(refreshKey);
    HttpServices.Post({
      url: buildURL(accountID),
      body: {},
      successCallback: (_data, message) => {
        setRefreshingAccount('');
        showToast(message || `${service} profile details refreshed successfully`, 's');
        fetchConnectedProfiles(true);
      },
      failureCallback: (message) => {
        setRefreshingAccount('');
        showToast(message || `Failed to refresh ${service} profile details`, 'e');
      },
    });
  };

  const openDeleteDialog = (service, account) => {
    const serviceLabels = {
      whatsapp: 'WhatsApp number',
      facebook: 'Facebook page',
      instagram: 'Instagram account',
    };
    setDeleteTarget({
      service,
      id: account.profile_id || account.id || '',
      label: account.display_name || account.phone_number || account.profile_id || serviceLabels[service],
      serviceLabel: serviceLabels[service],
    });
  };

  const closeDeleteDialog = () => {
    if (!deleteLoader) setDeleteTarget(null);
  };

  const confirmDeleteAccount = () => {
    if (!deleteTarget?.id) return;
    setDeleteLoader(true);
    HttpServices.Delete({
      url: userModuleURL.connectedProfileDelete(deleteTarget.service, encodeURIComponent(deleteTarget.id)),
      successCallback: (_data, message) => {
        setDeleteLoader(false);
        setDeleteTarget(null);
        showToast(message || `${deleteTarget.serviceLabel} deleted successfully`, 's');
        fetchConnectedProfiles(true);
        clearInactiveProfile?.(deleteTarget.service, deleteTarget.id);
      },
      failureCallback: (message) => {
        setDeleteLoader(false);
        showToast(message || `Failed to delete ${deleteTarget.serviceLabel}`, 'e');
      },
    });
  };

  const toggleInstagramAccount = (instagramID) => {
    setSelectedInstagramIDs((current) =>
      current.includes(instagramID) ? current.filter((id) => id !== instagramID) : [...current, instagramID]
    );
  };
  const toggleFacebookPage = (pageID) => {
    setSelectedFacebookPageIDs((current) =>
      current.includes(pageID) ? current.filter((id) => id !== pageID) : [...current, pageID]
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Card
        variant="outlined"
        sx={(theme) => ({
          p: { xs: 2.5, md: 3 },
          mb: 3,
          borderRadius: 1,
          borderColor: alpha(theme.palette.primary.main, 0.18),
          backgroundColor: alpha(theme.palette.primary.main, 0.04),
        })}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <Box
            sx={(theme) => ({
              width: 48,
              height: 48,
              borderRadius: 1,
              display: 'grid',
              placeItems: 'center',
              color: theme.palette.common.white,
              backgroundColor: theme.palette.primary.main,
            })}
          >
            <IntegrationInstructionsIcon />
          </Box>
          <Box>
            <Typography variant="h4" fontWeight={800}>
              {translate('integrations.title', { defaultValue: 'Integrations' })}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {translate('integrations.subtitle', {
                defaultValue:
                  'Connect customer channels to Brand2Chat. Website Widget is available now, with WhatsApp, Facebook, and Instagram coming soon.',
              })}
            </Typography>
          </Box>
        </Stack>
      </Card>

      <Tabs value={activeTab} onChange={(_event, value) => setActiveTab(value)} sx={{ mb: 2 }}>
        <Tab value="integrations" label="Integrations" />
        <Tab value="accounts" label="Integrated Accounts" />
      </Tabs>

      {activeTab === 'integrations' && (
        <Grid container spacing={2.5}>
          {channels.map((channel) => (
            <Grid item xs={12} sm={6} md={3} key={channel.key}>
              <IntegrationCard
                channel={{
                  ...channel,
                  comingSoon: channel.key === 'facebook' || channel.key === 'instagram' ? false : channel.comingSoon,
                  actionKey: channel.key === 'facebook' ? 'facebook.action' : channel.actionKey,
                  actionFallback: channel.key === 'facebook' ? 'Connect Facebook' : channel.actionFallback,
                  onClick:
                    channel.key === 'facebook'
                      ? handleFacebookConnect
                      : channel.key === 'instagram'
                      ? handleInstagramConnect
                      : undefined,
                }}
                onWhatsAppConnect={handleWhatsAppConnect}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {activeTab === 'accounts' && (
        <IntegratedAccounts
          whatsAppLoader={whatsAppLoader}
          facebookLoader={facebookLoader}
          instagramLoader={instagramLoader}
          connectedProfiles={connectedProfiles}
          connectedProfilesLoader={connectedProfilesLoader}
          onWhatsAppConnect={handleWhatsAppConnect}
          onWhatsAppReauthenticate={reauthenticateWhatsAppAccount}
          onFacebookConnect={handleFacebookConnect}
          onInstagramConnect={handleInstagramConnect}
          onFacebookReauthenticate={reauthenticateFacebookPage}
          onInstagramReauthenticate={reauthenticateInstagramAccount}
          onRefresh={refreshAccountProfile}
          refreshingAccount={refreshingAccount}
          onDelete={openDeleteDialog}
          onToggleEnable={updateAccountEnable}
        />
      )}

      <Dialog open={whatsAppPinDialogOpen} onClose={() => setWhatsAppPinDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Register WhatsApp number</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            fullWidth
            label="Two-step verification PIN"
            type="password"
            value={whatsAppPin}
            onChange={(event) => setWhatsAppPin(event.target.value.replace(/\D/g, '').slice(0, 6))}
            inputProps={{ inputMode: 'numeric', maxLength: 6 }}
            error={whatsAppPin.length > 0 && whatsAppPin.length !== 6}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWhatsAppPinDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={whatsAppPin.length !== 6 || whatsAppLoader || !whatsAppPendingAccountID}
            onClick={() => {
              setWhatsAppLoader(true);
              HttpServices.Post({
                url: userModuleURL.whatsappRegister(whatsAppPendingAccountID),
                body: { pin: whatsAppPin },
                successCallback: (_data, message) => {
                  setWhatsAppLoader(false);
                  setWhatsAppPin('');
                  setWhatsAppPendingAccountID(null);
                  setWhatsAppPinDialogOpen(false);
                  showToast(message || 'WhatsApp number registered successfully', 's');
                  fetchConnectedProfiles(true);
                },
                failureCallback: (message) => {
                  setWhatsAppLoader(false);
                  showToast(message || 'WhatsApp number registration failed', 'e');
                },
              });
            }}
          >
            Connect
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={closeDeleteDialog} fullWidth maxWidth="xs">
        <DialogTitle>Delete integrated account</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            Delete {deleteTarget?.label}? This removes the account from this brand and unsubscribes the webhook where
            Meta supports account-level unsubscribe.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleteLoader}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={confirmDeleteAccount} disabled={deleteLoader}>
            {deleteLoader ? <CircularProgress color="inherit" size={18} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={instagramDialogOpen} onClose={() => setInstagramDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Select Instagram accounts</DialogTitle>
        <DialogContent dividers>
          <List disablePadding>
            {instagramAccountOptions.map((account) => (
              <ListItemButton
                key={account.instagram_id}
                disabled={account.is_integrated}
                onClick={() => toggleInstagramAccount(account.instagram_id)}
                sx={{ borderRadius: 1, mb: 0.75 }}
              >
                <ListItemAvatar>
                  <Avatar src={account.picture_url || ''} sx={{ bgcolor: alpha('#E1306C', 0.14), color: '#E1306C' }}>
                    <InstagramIcon fontSize="small" />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography fontWeight={800}>
                        {account.username || account.name || 'Instagram Account'}
                      </Typography>
                      {account.is_integrated && <Chip size="small" color="warning" label="Already integrated" />}
                    </Stack>
                  }
                  secondary={account.name || account.instagram_id}
                />
                {!account.is_integrated && (
                  <Checkbox edge="end" checked={selectedInstagramIDs.includes(account.instagram_id)} tabIndex={-1} />
                )}
              </ListItemButton>
            ))}
            {!instagramAccountOptions.length && (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography fontWeight={800}>No Instagram accounts found</Typography>
              </Box>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInstagramDialogOpen(false)}>Cancel</Button>
          {instagramAuthPagination && instagramAccountOptions.length >= 10 && (
            <Button
              variant="outlined"
              onClick={loadNextInstagramAuthAccounts}
              disabled={instagramAccountPaginationLoader}
            >
              {instagramAccountPaginationLoader ? <CircularProgress color="inherit" size={18} /> : 'Next 10 accounts'}
            </Button>
          )}
          <Button
            variant="contained"
            onClick={integrateInstagramAccounts}
            disabled={instagramLoader || selectedInstagramIDs.length === 0}
          >
            {instagramLoader ? <CircularProgress color="inherit" size={18} /> : 'Integrate Accounts'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={facebookDialogOpen} onClose={() => setFacebookDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Select Facebook pages</DialogTitle>
        <DialogContent dividers>
          <List disablePadding>
            {facebookPageOptions.map((page) => (
              <ListItemButton
                key={page.page_id}
                disabled={page.is_integrated}
                onClick={() => toggleFacebookPage(page.page_id)}
                sx={{ borderRadius: 1, mb: 0.75 }}
              >
                <ListItemAvatar>
                  <Avatar src={page.picture_url || ''} sx={{ bgcolor: alpha('#1877F2', 0.14), color: '#1877F2' }}>
                    <FacebookIcon fontSize="small" />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography fontWeight={800}>{page.name || 'Facebook Page'}</Typography>
                      {page.is_integrated && <Chip size="small" color="warning" label="Already integrated" />}
                    </Stack>
                  }
                  secondary={page.category || page.page_id}
                />
                {!page.is_integrated && (
                  <Checkbox edge="end" checked={selectedFacebookPageIDs.includes(page.page_id)} tabIndex={-1} />
                )}
              </ListItemButton>
            ))}
            {!facebookPageOptions.length && (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography fontWeight={800}>No Facebook pages found</Typography>
              </Box>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFacebookDialogOpen(false)}>Cancel</Button>
          {facebookAuthPagination && facebookPageOptions.length >= 10 && (
            <Button variant="outlined" onClick={loadNextFacebookAuthPages} disabled={facebookPagePaginationLoader}>
              {facebookPagePaginationLoader ? <CircularProgress color="inherit" size={18} /> : 'Next 10 pages'}
            </Button>
          )}
          <Button
            variant="contained"
            onClick={integrateFacebookPages}
            disabled={facebookLoader || selectedFacebookPageIDs.length === 0}
          >
            {facebookLoader ? <CircularProgress color="inherit" size={18} /> : 'Integrate Pages'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
